import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { SearchView } from "../../../components";
import ListView from "../../../components/list-view";
import SelectableListView from "../../../components/selectable-list-view";
import { useTableView } from "@/hooks/useTableView";
import { Button } from "@/components/ui/button";
import { getAllInvoices } from "@/data/requests/invoice-requests";

const COUNTRY = "Azerbaijan";
const MAX_AMOUNT = 10;

const STATUS_OPTIONS = [
  { value: "Approved", label: "Approved" },
  { value: "Change Requested", label: "Change Requested" },
  { value: "Troubled", label: "Troubled" },
  { value: "Invoice Uploaded", label: "Invoice Uploaded" },
  { value: "Mail Sended", label: "Mail Sended" },
  { value: "Accounting", label: "Accounting" },
  { value: "Auto Created Invoice", label: "Auto Created Invoice" },
  { value: "OCR Completed", label: "OCR Completed" },
  { value: "LUCAREADY", label: "LUCA READY" },
  { value: "LUCA", label: "LUCA" },
  { value: "WAITING", label: "WAITING" },
  { value: "InBank", label: "InBank" },
  { value: "PAID-TIMEOUT", label: "PAID-TIMEOUT" },
  { value: "Paid", label: "Paid" },
  { value: "Not Paid", label: "Not Paid" },
  { value: "Return", label: "Return" },
  { value: "all", label: "All" },
];

const DEFAULT_SEARCH = { name: "", email: "", lucaId: "", status: "" };

export function AzerbaijanLowAmountCommissionsScreen() {
  const { isAdvancedTable } = useTableView();
  const navigate = useNavigate();
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("");
  const [searchParams, setSearchParams] = useState(DEFAULT_SEARCH);
  const [queryParams, setQueryParams] = useState(DEFAULT_SEARCH);

  const handleSearch = (item) => {
    const next = { ...searchParams, [item.key]: item.value };
    setSearchParams(next);
    setTimeout(() => {
      setQueryParams(next);
      setPage(1);
    }, 300);
  };

  const { data, isLoading } = useQuery({
    queryKey: [
      "azerbaijan-low-amount-commissions",
      page,
      pageSize,
      queryParams,
      sortColumn,
      sortDirection,
    ],
    queryFn: () =>
      getAllInvoices({
        page,
        pageSize,
        countryFilter: COUNTRY,
        maxAmount: MAX_AMOUNT,
        nameFilter: queryParams.name,
        emailNameFilter: queryParams.email,
        lucaIdFilter: queryParams.lucaId,
        statusFilter: queryParams.status,
        sortColumn,
        sortDirection,
      }),
  });

  const columns = [
    {
      key: "clientName",
      label: "Client Name",
      accessor: (item) => item.commission?.client?.name || "",
    },
    {
      key: "influencerName",
      label: "Influencer",
      accessor: (item) => item.commission?.influencerName || "",
    },
    {
      key: "commissionEmail",
      label: "Commission Email",
      accessor: (item) => item.commission?.email || "",
    },
    { key: "invoiceId", label: "Invoice ID", sortable: true },
    { key: "totalAmount", label: "Total Amount", sortable: true },
    { key: "currency", label: "Currency", sortable: true },
    {
      key: "country",
      label: "Country",
      accessor: (item) => item.commission?.country || "",
    },
    { key: "status", label: "Status", sortable: true },
    {
      key: "createdAt",
      label: "Created At",
      sortable: true,
      accessor: (item) =>
        item.createdAt ? new Date(item.createdAt).toLocaleDateString("tr-TR") : "",
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      render: (row) => (
        <Button
          size="sm"
          onClick={() =>
            navigate(
              `/payments/${row.commission?.paymentId}/invoices/${row.id}`
            )
          }
        >
          View
        </Button>
      ),
    },
  ];

  const defaultVisibleColumns = [
    "clientName",
    "commissionEmail",
    "totalAmount",
    "currency",
    "country",
    "status",
    "actions",
  ];

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const pageSettings = {
    title: `Azerbaijan Commissions Under $${MAX_AMOUNT}`,
    actions: [
      {
        name: "Back",
        color: "bg-red-100 hover:bg-red-400 hover:text-black text-red-800",
        icon: "add-icon",
        onClick: () => navigate("/dashboard"),
      },
    ],
  };

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      actions={pageSettings.actions}
      loading={isLoading}
    >
      <div className="px-4 py-2 text-sm text-muted-foreground">
        Showing commission details where country is{" "}
        <strong>{COUNTRY}</strong> and total amount is less than{" "}
        <strong>{MAX_AMOUNT}</strong> (currency ignored).
      </div>
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
        resetDefault={() => {
          setSearchParams(DEFAULT_SEARCH);
          setQueryParams(DEFAULT_SEARCH);
          setPage(1);
        }}
      />
      {isAdvancedTable ? (
        <SelectableListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={columns}
          columnVisibilityKey="azerbaijan-low-amount-columns"
          defaultVisibleColumnKeys={defaultVisibleColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSortChange={handleSort}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) =>
            navigate(`/payments/${item.commission?.paymentId}/invoices/${item.id}`)
          }
        />
      ) : (
        <ListView
          isLoading={isLoading}
          data={data?.data?.commissionDetails}
          columns={columns}
          totalItems={data?.data?.totalItems}
          enablePagination={true}
          itemsPerPage={pageSize}
          currentPage={page}
          onPageChange={setPage}
          onRowClick={(item) =>
            navigate(`/payments/${item.commission?.paymentId}/invoices/${item.id}`)
          }
        />
      )}
    </AppLayout>
  );
}
